// @flow
import {
    getAllUpcomingAuctionsUrl,
    getAuctioneerDirectoryUrl,
    getAuctionResultsUrl,
    getCookiePolicyUrl,
    getPrivacyUrl,
    getTermsAndConditionsUrl,
} from '../../../../utils/urls';
import classNames from 'classnames';
import { faFacebookF } from '@fortawesome/free-brands-svg-icons/faFacebookF';
import { faPinterest } from '@fortawesome/free-brands-svg-icons/faPinterest';
import { faTachometerAlt } from '@fortawesome/pro-solid-svg-icons/faTachometerAlt';
import { faTwitter } from '@fortawesome/free-brands-svg-icons/faTwitter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'react-intl';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components4/lib/Link/Link';
import React from 'react';
import styles from './Support.module.scss';

type Props = {
    baseUrl: string,
    classicBaseUrl: string,
    openFeedbackModal: Function,
    openHealthcheckModal: Function,
    trackClassicLinkClick: Function,
    year: number,
};

const Support = (props: Props) => {
    const { baseUrl, classicBaseUrl, openFeedbackModal, openHealthcheckModal, trackClassicLinkClick, year } = props;

    return (
        <div className={styles.support}>
            <div className={styles.container}>
                <div className={styles.column}>
                    <div className={styles.wrapper}>
                        <div className={styles['column-title']}>
                            <FormattedMessage id="company" />
                        </div>
                        <ul>
                            <li>
                                <Link route to="/about">
                                    <FormattedMessage id="about" />
                                </Link>
                            </li>
                            <li>
                                <Link
                                    target="_blank"
                                    to="https://careers.jobscore.com/careers/liveauctioneers"
                                    rel="noopener"
                                >
                                    <FormattedMessage id="careers" />
                                </Link>
                            </li>
                            <li>
                                <Link target="_blank" to={`${baseUrl}/news/`} rel="noopener">
                                    <FormattedMessage id="auction_central_news" />
                                </Link>
                            </li>
                            <li>
                                <Link target="_blank" to="https://help.liveauctioneers.com/" rel="noopener">
                                    <FormattedMessage id="support_center" />
                                </Link>
                            </li>
                            <li>
                                <Link target="_blank" onClick={openFeedbackModal} rel="noopener">
                                    <FormattedMessage id="feedback.footer" />
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>
                <div className={styles.column}>
                    <div className={styles.wrapper}>
                        <div className={styles['column-title']}>
                            <FormattedMessage id="winning" />
                        </div>
                        <ul>
                            <li>
                                <Link route to="/how-it-works">
                                    <FormattedMessage id="how_auctions_work" />
                                </Link>
                            </li>
                            <li>
                                <Link route to={getAllUpcomingAuctionsUrl()}>
                                    <FormattedMessage id="auction_calendar" />
                                </Link>
                            </li>
                            <li>
                                <Link route to={getAuctioneerDirectoryUrl()}>
                                    <FormattedMessage id="auction_house_directory" />
                                </Link>
                            </li>
                            <li>
                                <Link route to="/find-auctions-near-me">
                                    <FormattedMessage id="auctions_near_you" />
                                </Link>
                            </li>
                            <li>
                                <Link route to={getAuctionResultsUrl()}>
                                    <FormattedMessage id="auction_result_search" />
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>
                <div className={styles.column}>
                    <div className={styles.wrapper}>
                        <div className={styles['column-title']}>
                            <FormattedMessage id="selling" />
                        </div>
                        <ul>
                            <li>
                                <Link route to="/features">
                                    <FormattedMessage id="why_sell" />
                                </Link>
                            </li>
                            <li>
                                <Link route to="/seller-testimonials">
                                    <FormattedMessage id="seller_testimonials" />
                                </Link>
                            </li>
                            <li>
                                <a target="_blank" href={`${baseUrl}/pages/sell/`}>
                                    <FormattedMessage id="become_a_seller" />
                                </a>
                            </li>
                            <li>
                                <Link route to="/consignments">
                                    <FormattedMessage id="consign_an_item" />
                                </Link>
                            </li>
                            <li>
                                <Link
                                    target="_blank"
                                    to={`${classicBaseUrl}/user/login.html?url=/auctioneers/`}
                                    rel="nofollow noopener"
                                >
                                    <FormattedMessage id="auctioneer_sign_in" />
                                </Link>
                            </li>
                            <li>
                                <Link
                                    target="_blank"
                                    to="https://www.liveauctioneers.com/pages/resource-center/"
                                    rel="nofollow"
                                >
                                    <FormattedMessage id="seller_resource_center" />
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <div className={styles['container-using']}>
                <FormattedMessage id="using" />: &nbsp;
                <span className={styles['new-site']}>
                    <FormattedMessage id="the_new_liveauctioneers_site" />
                </span>{' '}
                &nbsp;
                <Link
                    className={styles['classic-link']}
                    onClick={trackClassicLinkClick}
                    rel="nofollow"
                    to={classicBaseUrl}
                >
                    <FormattedMessage id="classic_site" />
                </Link>
            </div>
            <div className={styles['social-icons']}>
                <Link
                    className={styles.icon}
                    to="https://twitter.com/liveauctioneers/"
                    name="LiveAuctioneers on Twitter"
                    target="_blank"
                    rel="noopener"
                >
                    <FontAwesomeIcon icon={faTwitter} />
                </Link>
                <Link
                    className={styles.icon}
                    name="LiveAuctioneers on Facebook"
                    to="https://www.facebook.com/LiveAuctioneers/"
                    target="_blank"
                    rel="noopener"
                >
                    <FontAwesomeIcon icon={faFacebookF} />
                </Link>
                <Link
                    className={styles.icon}
                    name="LiveAuctioneers on Pinterest"
                    to="https://www.pinterest.com/liveauctioneers/"
                    target="_blank"
                    rel="noopener"
                >
                    <FontAwesomeIcon icon={faPinterest} />
                </Link>
            </div>
            <div className={styles['container-copyright']}>
                <div className={styles.copyright}>
                    <span>©</span> <FormattedMessage id="copyright" values={{ year }} />
                    <Link route to={getCookiePolicyUrl()}>
                        <FormattedMessage id="cookie_policy" />
                    </Link>
                    <Link route to={getTermsAndConditionsUrl()}>
                        <FormattedMessage id="terms_and_conditions" />
                    </Link>
                    <Link route to={getPrivacyUrl()}>
                        <FormattedMessage id="privacy_policy" />
                    </Link>
                    <FontAwesomeIcon className={styles.health} icon={faTachometerAlt} onClick={openHealthcheckModal} />
                </div>
                <div className={classNames(styles.copyright, styles.google)}>
                    <FormattedMessage
                        id="footer.recaptcha.text"
                        values={{
                            privacy: (
                                <Link
                                    className={styles.googleLink}
                                    rel="nofollow noopener"
                                    target="_blank"
                                    to="https://policies.google.com/privacy"
                                >
                                    <FormattedMessage id="footer.recaptcha.privacy" />
                                </Link>
                            ),
                            tos: (
                                <Link
                                    className={styles.googleLink}
                                    rel="nofollow noopener"
                                    target="_blank"
                                    to="https://policies.google.com/terms"
                                >
                                    <FormattedMessage id="footer.recaptcha.tos" />
                                </Link>
                            ),
                        }}
                    />
                </div>
                <div className={styles.sitemap}>
                    <span className={styles['bold-title']}>
                        <FormattedMessage id="sitemap" />:
                    </span>
                    <Link to="https://www.liveauctioneers.com/browse/archive/page1" target="_blank" rel="noopener">
                        <FormattedMessage id="past_auctions" />
                    </Link>
                    <Link to="https://www.liveauctioneers.com/browse/" target="_blank" rel="noopener">
                        <FormattedMessage id="upcoming_auctions" />
                    </Link>
                    <Link to="https://www.liveauctioneers.com/search_auctioneers.html" target="_blank" rel="noopener">
                        <FormattedMessage id="auctioneer_directory" />
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default React.memo<Props>(Support, isEqual);
